import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { 
  Building2, 
  MapPin, 
  Phone, 
  Mail, 
  Globe, 
  Calendar,
  Edit,
  Save,
  X,
  Upload,
  Trash2,
  CreditCard,
  Plus
} from 'lucide-react'
import { Button, BankDetailForm } from '../components'
import { companyService } from '../services/companyService'
import { bankDetailService } from '../services'
import { getFullLogoUrl } from '../utils/urlUtils'
import { useAuth } from '../contexts/AuthContextTest'
import toast from 'react-hot-toast'

function CompanyPreview() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  const [company, setCompany] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isUpdating, setIsUpdating] = useState(false)
  const [isUploadingLogo, setIsUploadingLogo] = useState(false)
  const [isDeletingLogo, setIsDeletingLogo] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [selectedLogo, setSelectedLogo] = useState(null)
  const [logoPreview, setLogoPreview] = useState(null)
  const [bankDetails, setBankDetails] = useState([])
  const [isLoadingBankDetails, setIsLoadingBankDetails] = useState(false)
  const [showBankDetailModal, setShowBankDetailModal] = useState(false)
  const [editingBankDetail, setEditingBankDetail] = useState(null)
  const [isCreatingBankDetail, setIsCreatingBankDetail] = useState(false)
  const [isUpdatingBankDetail, setIsUpdatingBankDetail] = useState(false)

  const { register, handleSubmit, reset, formState: { errors } } = useForm()

  // Fetch company data
  const fetchCompany = async () => {
    try {
      setIsLoading(true)
      const response = await companyService.getById(parseInt(id))
      setCompany(response.data.company)
      console.log('Company fetched:', response.data.company)
    } catch (error) {
      console.error('Error fetching company:', error)
      toast.error('Failed to fetch company details')
    } finally {
      setIsLoading(false)
    }
  }

  // Debug logging
  console.log('CompanyPreview Debug:', {
    id,
    company,
    isEditing,
    isUploadingLogo,
    isDeletingLogo
  })

  // Fetch bank details for the company
  const fetchBankDetails = async () => {
    if (!id) return
    try {
      setIsLoadingBankDetails(true)
      const response = await bankDetailService.getAll({ companyId: parseInt(id) })
      setBankDetails(response.bankDetails || response.data?.bankDetails || [])
    } catch (error) {
      console.error('Error fetching bank details:', error)
      toast.error('Failed to fetch bank details')
    } finally {
      setIsLoadingBankDetails(false)
    }
  }

  // Fetch company data on component mount
  useEffect(() => {
    if (id) {
      fetchCompany()
      fetchBankDetails()
    }
  }, [id])

  // Reset form when company data changes
  useEffect(() => {
    if (company) {
      reset(company)
    }
  }, [company, reset])

  // Handle form submission
  const onSubmit = async (data) => {
    try {
      setIsUpdating(true)
      const response = await companyService.update(parseInt(id), data)
      setCompany(response.data.company)
      toast.success('Company updated successfully!')
      setIsEditing(false)
    } catch (error) {
      console.error('Error updating company:', error)
      toast.error('Failed to update company')
    } finally {
      setIsUpdating(false)
    }
  }

  // Handle logo upload
  const handleLogoUploadEvent = async (event) => {
    console.log('Logo upload event triggered:', event.target.files)
    const file = event.target.files[0]
    if (file) {
      console.log('File selected:', file.name, file.size, file.type)
      setSelectedLogo(file)
      const reader = new FileReader()
      reader.onload = (e) => setLogoPreview(e.target.result)
      reader.readAsDataURL(file)

      // Auto-upload logo
      try {
        setIsUploadingLogo(true)
        console.log('Starting logo upload for company ID:', parseInt(id))
        const response = await companyService.uploadLogo(parseInt(id), file)
        console.log('Logo upload successful:', response)
        setCompany(prev => ({ ...prev, logo: response.data.logo }))
        setSelectedLogo(null)
        setLogoPreview(null)
        toast.success('Logo uploaded successfully!')
      } catch (error) {
        console.error('Error uploading logo:', error)
        toast.error('Failed to upload logo')
      } finally {
        setIsUploadingLogo(false)
      }
    } else {
      console.log('No file selected')
    }
  }

  // Handle logo deletion
  const handleLogoDeleteEvent = async () => {
    if (window.confirm('Are you sure you want to delete this logo?')) {
      try {
        setIsDeletingLogo(true)
        await companyService.deleteLogo(parseInt(id))
        setCompany(prev => ({ ...prev, logo: null }))
        toast.success('Logo deleted successfully!')
      } catch (error) {
        console.error('Error deleting logo:', error)
        toast.error('Failed to delete logo')
      } finally {
        setIsDeletingLogo(false)
      }
    }
  }

  // Handle edit toggle
  const handleEditToggle = () => {
    if (isEditing) {
      reset(company) // Reset form to original data
    }
    setIsEditing(!isEditing)
  }

  // Handle bank detail operations
  const handleBankDetailSubmit = async (data) => {
    try {
      if (editingBankDetail) {
        setIsUpdatingBankDetail(true)
        await bankDetailService.update(editingBankDetail.id, {
          ...data,
          companyId: parseInt(id)
        })
        toast.success('Bank detail updated successfully!')
      } else {
        setIsCreatingBankDetail(true)
        await bankDetailService.create({
          ...data,
          companyId: parseInt(id)
        })
        toast.success('Bank detail created successfully!')
      }
      setShowBankDetailModal(false)
      setEditingBankDetail(null)
      await fetchBankDetails()
    } catch (error) {
      console.error('Error saving bank detail:', error)
      toast.error(error.response?.data?.message || 'Failed to save bank detail')
    } finally {
      setIsCreatingBankDetail(false)
      setIsUpdatingBankDetail(false)
    }
  }

  const handleBankDetailEdit = (bankDetail) => {
    setEditingBankDetail(bankDetail)
    setShowBankDetailModal(true)
  }

  const handleBankDetailDelete = async (bankDetail) => {
    if (window.confirm('Are you sure you want to delete this bank detail?')) {
      try {
        await bankDetailService.delete(bankDetail.id)
        toast.success('Bank detail deleted successfully!')
        await fetchBankDetails()
      } catch (error) {
        console.error('Error deleting bank detail:', error)
        toast.error('Failed to delete bank detail')
      }
    }
  }

  const handleBankDetailClose = () => {
    setShowBankDetailModal(false)
    setEditingBankDetail(null)
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (!company) {
    return (
      <div className="text-center py-12">
        <Building2 className="mx-auto h-12 w-12 text-secondary-400" />
        <h3 className="mt-2 text-sm font-medium text-secondary-900">Company not found</h3>
        <p className="mt-1 text-sm text-secondary-500">The company you're looking for doesn't exist.</p>
        <div className="mt-6">
          <Button onClick={() => navigate('/companies')} variant="outline">
            Back to Companies
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-secondary-50">
      {/* Header Section */}
      <div className="bg-white border-b border-secondary-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center space-x-4">
              <div className="flex-shrink-0">
                <div className="h-12 w-12 bg-primary-100 rounded-lg flex items-center justify-center">
                  <Building2 className="h-6 w-6 text-primary-600" />
                </div>
              </div>
              <div>
                <h1 className="text-4xl font-bold text-secondary-900">
                  {company?.name || 'Company Details'}
                </h1>
                <p className="mt-1 text-base text-secondary-600">
                  {isEditing ? 'Edit company information' : 'View and manage company information'}
                </p>
              </div>
            </div>
            <div className="flex space-x-3">
              <Button
                onClick={handleEditToggle}
                variant={isEditing ? "outline" : "primary"}
                disabled={isUpdating}
                size="md"
              >
                {isEditing ? (
                  <>
                    <X className="h-4 w-4 mr-2" />
                    Cancel
                  </>
                ) : (
                  <>
                    <Edit className="h-4 w-4 mr-2" />
                    Edit Company
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-sm border border-secondary-200 p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="h-8 w-8 bg-primary-100 rounded-lg flex items-center justify-center">
                  <Building2 className="h-4 w-4 text-primary-600" />
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-secondary-600">Company</p>
                <p className="text-lg font-semibold text-secondary-900">{company?.name || 'N/A'}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-sm border border-secondary-200 p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="h-8 w-8 bg-green-100 rounded-lg flex items-center justify-center">
                  <Mail className="h-4 w-4 text-green-600" />
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-secondary-600">Email</p>
                <p className="text-sm font-semibold text-secondary-900 truncate">{company?.email || 'Not provided'}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-sm border border-secondary-200 p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="h-8 w-8 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Phone className="h-4 w-4 text-blue-600" />
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-secondary-600">Phone</p>
                <p className="text-sm font-semibold text-secondary-900">{company?.phone || 'Not provided'}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-sm border border-secondary-200 p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className={`h-8 w-8 rounded-lg flex items-center justify-center ${
                  company?.isActive ? 'bg-green-100' : 'bg-red-100'
                }`}>
                  <div className={`h-4 w-4 rounded-full ${
                    company?.isActive ? 'bg-green-600' : 'bg-red-600'
                  }`}></div>
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-secondary-600">Status</p>
                <p className={`text-sm font-semibold ${
                  company?.isActive ? 'text-green-900' : 'text-red-900'
                }`}>
                  {company?.isActive ? 'Active' : 'Inactive'}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Company Logo Section */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-sm border border-secondary-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-secondary-200">
                <h3 className="text-lg font-semibold text-secondary-900 flex items-center">
                  <Building2 className="h-5 w-5 mr-2 text-primary-600" />
                  Company Logo
                </h3>
              </div>
              <div className="p-6">
                <div className="flex flex-col items-center space-y-6">
                  {company.logo ? (
                    <div className="relative group">
                      <img
                        src={getFullLogoUrl(company.logo)}
                        alt={`${company.name} logo`}
                        className="h-40 w-40 object-cover rounded-xl border-2 border-secondary-200 shadow-sm"
                        onError={(e) => {
                          console.error('Image load error:', e);
                          e.target.style.display = 'none';
                        }}
                        onLoad={() => console.log('Image loaded successfully')}
                      />
                      {isEditing && (
                        <button
                          onClick={handleLogoDeleteEvent}
                          className="absolute -top-2 -right-2 bg-danger-500 text-white rounded-full p-2 hover:bg-danger-600 shadow-lg transition-all duration-200"
                          disabled={isUploadingLogo}
                          title="Delete Logo"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="h-40 w-40 bg-gradient-to-br from-secondary-100 to-secondary-200 rounded-xl flex items-center justify-center border-2 border-dashed border-secondary-300">
                      <Building2 className="h-20 w-20 text-secondary-400" />
                    </div>
                  )}
                  
                  {isEditing && (
                    <div className="w-full">
                      <label className="w-full btn-outline cursor-pointer flex items-center justify-center py-3 px-4 rounded-lg border-2 border-dashed border-primary-300 hover:border-primary-400 hover:bg-primary-50 transition-all duration-200">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleLogoUploadEvent}
                          className="hidden"
                        />
                        <Upload className="h-5 w-5 mr-2" />
                        {isUploadingLogo ? 'Uploading...' : 'Upload New Logo'}
                      </label>
                      <p className="text-xs text-secondary-500 mt-2 text-center">
                        Recommended: 400x400px, PNG or JPG
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Company Details Section */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-sm border border-secondary-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-secondary-200">
                <h3 className="text-lg font-semibold text-secondary-900 flex items-center">
                  <Building2 className="h-5 w-5 mr-2 text-primary-600" />
                  Company Information
                </h3>
              </div>
              <div className="p-6">
              
              {isEditing ? (
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-secondary-700 mb-1">
                        Company Name *
                      </label>
                      <input
                        {...register('name', { required: 'Company name is required' })}
                        className="input"
                        placeholder="Enter company name"
                      />
                      {errors.name && (
                        <p className="text-danger-600 text-sm mt-1">{errors.name.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-secondary-700 mb-1">
                        Email
                      </label>
                      <input
                        {...register('email')}
                        type="email"
                        className="input"
                        placeholder="Enter email"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-secondary-700 mb-1">
                        Phone
                      </label>
                      <input
                        {...register('phone')}
                        className="input"
                        placeholder="Enter phone number"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-secondary-700 mb-1">
                        Website
                      </label>
                      <input
                        {...register('website')}
                        className="input"
                        placeholder="Enter website URL"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1">
                      Address
                    </label>
                    <textarea
                      {...register('address')}
                      className="input"
                      rows={3}
                      placeholder="Enter address"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-secondary-700 mb-1">
                        City
                      </label>
                      <input
                        {...register('city')}
                        className="input"
                        placeholder="Enter city"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-secondary-700 mb-1">
                        State
                      </label>
                      <input
                        {...register('state')}
                        className="input"
                        placeholder="Enter state"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-secondary-700 mb-1">
                        Pincode
                      </label>
                      <input
                        {...register('pincode')}
                        className="input"
                        placeholder="Enter pincode"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-secondary-700 mb-1">
                        GST Number
                      </label>
                      <input
                        {...register('gstNumber')}
                        className="input"
                        placeholder="Enter GST number"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-secondary-700 mb-1">
                        PAN Number
                      </label>
                      <input
                        {...register('panNumber')}
                        className="input"
                        placeholder="Enter PAN number"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1">
                      Status
                    </label>
                    <select {...register('isActive')} className="input">
                      <option value={true}>Active</option>
                      <option value={false}>Inactive</option>
                    </select>
                  </div>

                  <div className="flex justify-end space-x-3">
                    <Button
                      type="button"
                      onClick={handleEditToggle}
                      variant="outline"
                      disabled={isUpdating}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="primary"
                      disabled={isUpdating}
                    >
                      {isUpdating ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save className="h-4 w-4 mr-2" />
                          Save Changes
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-secondary-700">Company Name</label>
                      <p className="mt-1 text-sm text-secondary-900">{company.name || 'Not provided'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-secondary-700">Email</label>
                      <p className="mt-1 text-sm text-secondary-900">{company.email || 'Not provided'}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-secondary-700">Phone</label>
                      <p className="mt-1 text-sm text-secondary-900">{company.phone || 'Not provided'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-secondary-700">Website</label>
                      <p className="mt-1 text-sm text-secondary-900">
                        {company.website ? (
                          <a href={company.website} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:text-primary-500">
                            {company.website}
                          </a>
                        ) : 'Not provided'}
                      </p>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-secondary-700">Address</label>
                    <p className="mt-1 text-sm text-secondary-900">{company.address || 'Not provided'}</p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-secondary-700">Status</label>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        company.isActive 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {company.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-secondary-700">Created</label>
                      <p className="mt-1 text-sm text-secondary-900">
                        {company.createdAt ? new Date(company.createdAt).toLocaleDateString() : 'Not available'}
                      </p>
                    </div>
                  </div>

                  {/* Additional Company Details */}
                  {(company.gstNumber || company.panNumber) && (
                    <div className="border-t border-secondary-200 pt-4">
                      <h4 className="text-sm font-medium text-secondary-900 mb-3">Tax Information</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {company.gstNumber && (
                          <div>
                            <label className="block text-sm font-medium text-secondary-700">GST Number</label>
                            <p className="mt-1 text-sm text-secondary-900">{company.gstNumber}</p>
                          </div>
                        )}
                        {company.panNumber && (
                          <div>
                            <label className="block text-sm font-medium text-secondary-700">PAN Number</label>
                            <p className="mt-1 text-sm text-secondary-900">{company.panNumber}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Location Details */}
                  {(company.city || company.state || company.pincode) && (
                    <div className="border-t border-secondary-200 pt-4">
                      <h4 className="text-sm font-medium text-secondary-900 mb-3 flex items-center">
                        <MapPin className="h-4 w-4 mr-1" />
                        Location Details
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {company.city && (
                          <div>
                            <label className="block text-sm font-medium text-secondary-700">City</label>
                            <p className="mt-1 text-sm text-secondary-900">{company.city}</p>
                          </div>
                        )}
                        {company.state && (
                          <div>
                            <label className="block text-sm font-medium text-secondary-700">State</label>
                            <p className="mt-1 text-sm text-secondary-900">{company.state}</p>
                          </div>
                        )}
                        {company.pincode && (
                          <div>
                            <label className="block text-sm font-medium text-secondary-700">Pincode</label>
                            <p className="mt-1 text-sm text-secondary-900">{company.pincode}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
              </div>
            </div>
          </div>
        </div>

        {/* Bank Details Section */}
        <div className="mt-8">
          <div className="bg-white rounded-xl shadow-sm border border-secondary-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-secondary-200 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-secondary-900 flex items-center">
                <CreditCard className="h-5 w-5 mr-2 text-primary-600" />
                Bank Details
              </h3>
              {user?.role?.id === 1 && (
                <Button
                  onClick={() => {
                    setEditingBankDetail(null)
                    setShowBankDetailModal(true)
                  }}
                  variant="primary"
                  size="sm"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Bank Detail
                </Button>
              )}
            </div>
            <div className="p-6">
              {isLoadingBankDetails ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                </div>
              ) : bankDetails.length === 0 ? (
                <div className="text-center py-8">
                  <CreditCard className="mx-auto h-12 w-12 text-secondary-400" />
                  <h3 className="mt-2 text-sm font-medium text-secondary-900">No bank details</h3>
                  <p className="mt-1 text-sm text-secondary-500">Get started by adding a bank detail.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {bankDetails.map((bankDetail) => (
                    <div
                      key={bankDetail.id}
                      className="border border-secondary-200 rounded-lg p-4 hover:border-primary-300 transition-colors"
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <div className="h-10 w-10 bg-primary-100 rounded-lg flex items-center justify-center">
                              <Building2 className="h-5 w-5 text-primary-600" />
                            </div>
                            <div>
                              <h4 className="text-sm font-semibold text-secondary-900">
                                {bankDetail.bankName}
                              </h4>
                              <p className="text-xs text-secondary-500">{bankDetail.branchName}</p>
                            </div>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                            <div>
                              <p className="text-xs text-secondary-500">Account Number</p>
                              <p className="text-sm font-medium text-secondary-900">
                                {bankDetail.accountNumber}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-secondary-500">IFSC Code</p>
                              <p className="text-sm font-medium text-secondary-900 font-mono">
                                {bankDetail.ifscCode}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-secondary-500">Status</p>
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                bankDetail.isActive 
                                  ? 'bg-green-100 text-green-800' 
                                  : 'bg-red-100 text-red-800'
                              }`}>
                                {bankDetail.isActive ? 'Active' : 'Inactive'}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2 ml-4">
                          <button
                            onClick={() => handleBankDetailEdit(bankDetail)}
                            className="text-primary-600 hover:text-primary-900 p-2"
                            title="Edit"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          {user?.role?.id === 1 && (
                            <button
                              onClick={() => handleBankDetailDelete(bankDetail)}
                              className="text-danger-600 hover:text-danger-900 p-2"
                              title="Delete"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Bank Detail Form Modal */}
      <BankDetailForm
        isOpen={showBankDetailModal}
        editingBankDetail={editingBankDetail}
        onClose={handleBankDetailClose}
        onSubmit={handleBankDetailSubmit}
        isLoading={isCreatingBankDetail || isUpdatingBankDetail}
      />
    </div>
  )
}

export default CompanyPreview
